import React, { useEffect } from 'react'
import Swal from 'sweetalert2'
import { useCustomReducer } from '../../../base/customHooks'
import { BaseComponentWithContextProps, componentWithContext } from '../../../base/customHooks/componentHOC'
import IPButton from '../../../components/IPButton'
import IPInput from '../../../components/IPInput'
import { IPInputDataPattern } from '../../../components/IPInput/IPInput'
import InputMask from '../../../components/InputMask'
import InputCampaign from '../../../components/InputCampaign'
import { Spinner } from '../../../components/Spinner'
import IPSelectBox, { ISelectOption } from '../../../components/IPSelectBox/IPSelectBox'
import { GetCompaniesApi } from '../../../layouts/components/client/services/getCompaniesApi'
import { GetExchangeRatesApi } from '../../../layouts/components/client/services/getExchangeRatesApi'
import { IExchangeRate } from '../../../layouts/components/client/models'
import { GetDealerMarkupsApi } from '../../MyCard/services/getDealerMarkupsApi'
import { GetSerialNumberCheckResponse } from '../MyOrders/models/response/getOrderListResponseModel'
import { GetSerialNumberCheck } from '../MyOrders/models/request/getMyOrderRequest'
import {
  CreditResultData,
  ErrorCodes,
  ErrorMessages,
  SerialCreditApplicationRequest,
  SerialCreditProductItem,
  StepType,
  PriceLimitCategory,
  CheckCreditData,
} from './models/models'
import { ApplySerialCreditApi } from './services/applySerialCreditApi'
import { CancelSerialCreditApi } from './services/cancelSerialCreditApi'
import { GetSerialNumberCheckApi } from './services/getSerialNumberCheckApi'
import { CategoryPriceLimitApi } from './services/categoryPriceLimitApi'
import styles from './SerialCreditApplication.module.scss'
import { CheckSerialCreditApi } from './services/checkSerialCreditApi'

// Birden fazla ürün için genişletilmiş ürün bilgisi
interface ProductWithCreditInfo extends GetSerialNumberCheckResponse {
  creditAmount: number // Bu ürün için kredi tutarı (orijinal para birimi)
  creditAmountTL: number // TL karşılığı
  inputCurrency: 'ORIGINAL' | 'TL' // Bu ürün için giriş para birimi
  isManualEntry?: boolean // Manuel olarak girildi mi (kar marjı uygulanmayacak)
}

interface SerialCreditState {
  step: StepType
  serialNumber: string // Yeni seri no girişi için
  // Manuel ürün girişi alanları
  manualProductCode: string
  manualProductName: string
  manualProductPrice: string // Fiyat string olarak (input değer)
  manualProductCurrency: string // Para birimi (USD, EUR, TL)
  manualProductCategory: string // Kategori (telefon, tablet, bilgisayar)
  // Kategori fiyat limitleri
  categoryPriceLimits: PriceLimitCategory[]
  // Birden fazla ürün desteği
  products: ProductWithCreditInfo[]
  // Eski alanlar geriye dönük uyumluluk için (ilk ürün referansı)
  serialNumberData: GetSerialNumberCheckResponse | null
  customerGsmNumber: string
  identitNumber: string
  dob: string
  creditAmount: number // Toplam kredi tutarı (ilk ürün veya toplam)
  creditAmountTL: number // Toplam TL karşılığı
  inputCurrency: 'ORIGINAL' | 'TL' // Kullanıcının tutar girdiği para birimi
  loading: boolean
  error: string | null
  errorCode: string | null
  companyOptions: ISelectOption[]
  companyValue?: ISelectOption
  companyCode: string
  applicationId: string
  exchangeRate: IExchangeRate | null
  cancelLoading: boolean
  pendingOrderId: string | null
  pendingRedirectUrl: string | null
  // Kredi sonuç bilgileri
  resultData: CreditResultData | null
  profitMarginRate: number // API'den çekilen maksimum kar marjı oranı
  minProfitMarginRate: number // API'den çekilen minimum kar marjı oranı
  checkCreditData?: CheckCreditData[] | null
}

function SerialCreditApplication(props: BaseComponentWithContextProps) {
  const getInitialState = (): SerialCreditState => {
    return {
      step: 'serial_input',
      serialNumber: '',
      dob: '',
      identitNumber: '',
      manualProductCode: '',
      manualProductName: '',
      manualProductPrice: '',
      manualProductCurrency: 'TL',
      manualProductCategory: '',
      categoryPriceLimits: [],
      products: [], // Birden fazla ürün desteği
      serialNumberData: null,
      customerGsmNumber: '',
      creditAmount: 0,
      creditAmountTL: 0,
      inputCurrency: 'ORIGINAL', // Varsayılan olarak ürün para birimi
      loading: false,
      error: null,
      errorCode: null,
      companyOptions: [],
      companyCode: '',
      applicationId: '',
      exchangeRate: null,
      cancelLoading: false,
      pendingOrderId: null,
      pendingRedirectUrl: null,
      resultData: null,
      profitMarginRate: 0.2, // Varsayılan %20, API'den override edilecek
      minProfitMarginRate: 0, // Varsayılan %0, API'den override edilecek
    }
  }

  const [state, dispatchState] = useCustomReducer<SerialCreditState>(getInitialState())

  // postMessage listener - Compay ödeme sonucu için
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Güvenlik kontrolü - sadece güvenilir origin'lerden gelen mesajları kabul et
      const allowedOrigins = [
        window.location.origin,
        'https://test.compaypayment.com',
        'https://compaypayment.com',
        'https://cptest.asseco-see.com.tr',
      ]

      // Origin kontrolü (development için esnek)
      if (!allowedOrigins.includes(event.origin) && !event.origin.includes('localhost')) {
        return
      }

      if (event.data.type === 'serial-credit-success') {
        // Başarılı ödeme
        dispatchState({
          step: 'result_success',
          loading: false,
          resultData: {
            orderId: event.data.orderId,
            authCode: event.data.authCode,
            refNum: event.data.refNum,
            amount: event.data.amount,
          },
        })
      } else if (event.data.type === 'serial-credit-fail') {
        // Başarısız ödeme
        dispatchState({
          step: 'result_fail',
          loading: false,
          resultData: {
            orderId: event.data.orderId,
            errorMessage: event.data.errorMessage,
          },
        })
      } else if (event.data.type === 'serial-credit-pending') {
        // Beklemede (yeni müşteri akışı - banka onboarding devam ediyor)
        dispatchState({
          step: 'result_pending',
          loading: false,
          resultData: {
            orderId: event.data.orderId,
            errorMessage: event.data.message,
          },
        })
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    //getCompanies()
    getExchangeRates()
    getDealerMarkups()
    getCategoryPriceLimits()
  }, [])

  const getExchangeRates = async () => {
    try {
      const api = new GetExchangeRatesApi({})
      const res = await api.getExhangeRates()
      if (res) {
        dispatchState({ exchangeRate: res })
      }
    } catch (err: any) {
      console.error('Error fetching exchange rates:', err)
    }
  }

  const getDealerMarkups = async () => {
    try {
      const api = new GetDealerMarkupsApi({})
      const res = await api.getDealerMarkups()
      if (res && res.MaxAllowedMarkupPercentage !== undefined) {
        // MaxAllowedMarkupPercentage'i 0-100 aralığından 0-1 aralığına çevir
        const marginRate = res.MaxAllowedMarkupPercentage / 100
        const minMarginRate = (res.MinAllowedMarkupPercentage ?? 0) / 100
        dispatchState({ profitMarginRate: marginRate, minProfitMarginRate: minMarginRate })
      }
    } catch (err: any) {
      console.error('Error fetching dealer markups:', err)
      // Hata durumunda varsayılan değer (0.2) kullanılır
    }
  }

  const getCategoryPriceLimits = async () => {
    try {
      const api = new CategoryPriceLimitApi({})
      const res = await api.getLimits()
      if (res && res.length > 0) {
        dispatchState({ categoryPriceLimits: res })
      }
    } catch (err: any) {
      console.error('Error fetching category price limits:', err)
      // Hata durumunda limit olmayacak
    }
  }

  // Maksimum kredi tutarını hesapla (base fiyat + kar marjı) - tek ürün için
  const getMaxCreditAmount = (): number => {
    if (!state.serialNumberData) return 0
    return state.serialNumberData.NETWRBR * (1 + state.profitMarginRate)
  }

  // Belirli bir ürün için maksimum kredi tutarını hesapla
  const getMaxCreditAmountForProduct = (product: ProductWithCreditInfo): number => {
    // Manuel olarak girilmiş ürünler için kar marjı uygulanmaz
    if (product.isManualEntry) {
      return product.NETWRBR
    }
    return product.NETWRBR * (1 + state.profitMarginRate)
  }

  // Belirli bir ürün için minimum kredi tutarını hesapla (sadece seri no ile bulunan ürünler)
  const getMinCreditAmountForProduct = (product: ProductWithCreditInfo): number => {
    if (product.isManualEntry) {
      return 0 // Manuel ürünler için minimum sınır yok
    }
    return product.NETWRBR * (1 + state.minProfitMarginRate)
  }

  // Tüm ürünlerin toplam maksimum kredi tutarını hesapla
  const getTotalMaxCreditAmount = (): number => {
    if (state.products.length === 0) return 0
    return state.products.reduce((total, product) => {
      // Manuel olarak girilmiş ürünler için kar marjı uygulanmaz
      const maxAmount = product.isManualEntry ? product.NETWRBR : product.NETWRBR * (1 + state.profitMarginRate)
      return total + maxAmount
    }, 0)
  }

  // Tüm ürünlerin toplam kredi tutarını hesapla (girilen değerler)
  const getTotalCreditAmount = (): number => {
    if (state.products.length === 0) return state.creditAmount
    return state.products.reduce((total, product) => total + product.creditAmount, 0)
  }

  // Tüm ürünlerin toplam TL karşılığını hesapla
  const getTotalCreditAmountTL = (): number => {
    if (state.products.length === 0) return state.creditAmountTL
    return state.products.reduce((total, product) => total + product.creditAmountTL, 0)
  }

  // String kur değerini number'a çevir (Türkçe format desteği: 42,4576 -> 42.4576)
  const parseExchangeRate = (rateStr: string): number => {
    if (!rateStr) return 1
    // Virgülü noktaya çevir
    const normalized = rateStr.replace(',', '.')
    return parseFloat(normalized) || 1
  }

  // Döviz kurunu al
  const getExchangeRateValue = (currencyType: string): number => {
    if (!state.exchangeRate) return 1
    const currency = currencyType?.toUpperCase()
    if (currency === 'USD' || currency === 'DOLAR') {
      return parseExchangeRate(state.exchangeRate.Usd)
    }
    if (currency === 'EUR' || currency === 'EURO') {
      return parseExchangeRate(state.exchangeRate.Eur)
    }
    return 1 // TL için
  }

  // const getCompanies = async () => {
  //   try {
  //     const res = await new GetCompaniesApi({}).getCompanyApi()
  //     if (res) {
  //       const data: ISelectOption[] = []
  //       res.forEach(item => {
  //         if (item.Name === 'Index Group') return
  //         data.push({ value: item.Code, label: item.Name })
  //       })
  //       dispatchState({
  //         companyOptions: data,
  //         companyValue: res.length === 1 ? data[0] : undefined,
  //         companyCode: res.length === 1 ? data[0].value.toString() : '',
  //       })
  //     }
  //   } catch (err: any) {
  //     console.error('Error fetching companies:', err)
  //   }
  // }

  const validateSerialNumber = (): boolean => {
    if (!state.serialNumber || state.serialNumber.trim() === '') {
      dispatchState({ error: ErrorMessages.EMPTY_SERIAL })
      return false
    }
    if (state.serialNumber.trim().length < 5) {
      dispatchState({ error: ErrorMessages.MIN_SERIAL_LENGTH })
      return false
    }
    // Şirket kontrolü - companyCode veya companyValue'dan birini kontrol et
    // const selectedCompanyCode = state.companyCode || (state.companyValue?.value ? String(state.companyValue.value) : '')
    // if (!selectedCompanyCode) {
    //   Swal.fire({
    //     icon: 'error',
    //     title: 'Lütfen şirket seçiniz',
    //     showConfirmButton: true,
    //     allowOutsideClick: false,
    //   })
    //   return false
    // }
    return true
  }

  const validateGsmNumber = (): boolean => {
    const gsm = state.customerGsmNumber.replace(/\s/g, '').replace(/_/g, '')
    if (!gsm || gsm.length !== 10) {
      dispatchState({ error: ErrorMessages.INVALID_GSM })
      return false
    }
    if (!gsm.startsWith('5')) {
      dispatchState({ error: ErrorMessages.INVALID_GSM })
      return false
    }
    return true
  }

  const validateCreditAmount = (): boolean => {
    // Birden fazla ürün varsa her birini kontrol et
    if (state.products.length > 0) {
      for (const product of state.products) {
        const maxAmount = getMaxCreditAmountForProduct(product)
        const minAmount = getMinCreditAmountForProduct(product)
        const exchangeRateValue = getExchangeRateValue(product.CurrencyType)
        const currencyType = product.CurrencyType?.toUpperCase()
        const isForeignCurrency =
          currencyType === 'USD' || currencyType === 'DOLAR' || currencyType === 'EUR' || currencyType === 'EURO'

        const tolerance = 0.01

        if (product.creditAmount <= 0) {
          dispatchState({ error: `${product.SeriNo} için kredi tutarı 0'dan büyük olmalıdır.` })
          return false
        }

        // Minimum kontrol (sadece seri no ile bulunan ürünler için)
        if (!product.isManualEntry && product.creditAmount < minAmount - tolerance) {
          if (isForeignCurrency) {
            const minAmountTL = minAmount * exchangeRateValue
            dispatchState({
              error: `${product.SeriNo} için kredi tutarı minimum ${formatCurrency(
                minAmount,
                product.CurrencyType
              )} (≈ ${formatCurrency(minAmountTL, 'TL')}) olmalıdır.`,
            })
          } else {
            dispatchState({
              error: `${product.SeriNo} için kredi tutarı minimum ${formatCurrency(minAmount, 'TL')} olmalıdır.`,
            })
          }
          return false
        }

        // Maksimum kontrol
        if (product.creditAmount > maxAmount + tolerance) {
          if (isForeignCurrency) {
            const maxAmountTL = maxAmount * exchangeRateValue
            dispatchState({
              error: `${product.SeriNo} için kredi tutarı maksimum ${formatCurrency(
                maxAmount,
                product.CurrencyType
              )} (≈ ${formatCurrency(maxAmountTL, 'TL')}) olabilir.`,
            })
          } else {
            dispatchState({
              error: `${product.SeriNo} için kredi tutarı maksimum ${formatCurrency(maxAmount, 'TL')} olabilir.`,
            })
          }
          return false
        }
      }
      return true
    }

    // Eski tek ürün mantığı (geriye dönük uyumluluk)
    if (!state.serialNumberData) return false

    const maxAmount = getMaxCreditAmount()
    const exchangeRateValue = getExchangeRateValue(state.serialNumberData.CurrencyType)
    const maxAmountTL = maxAmount * exchangeRateValue
    const currencyType = state.serialNumberData.CurrencyType?.toUpperCase()
    const isForeignCurrency =
      currencyType === 'USD' || currencyType === 'DOLAR' || currencyType === 'EUR' || currencyType === 'EURO'

    const tolerance = 0.01
    if (state.creditAmount <= 0 || state.creditAmount > maxAmount + tolerance) {
      if (isForeignCurrency) {
        dispatchState({
          error: `Kredi tutarı maksimum ${formatCurrency(
            maxAmount,
            state.serialNumberData.CurrencyType
          )} (≈ ${formatCurrency(maxAmountTL, 'TL')}) olabilir.`,
        })
      } else {
        dispatchState({
          error: `Kredi tutarı maksimum ${formatCurrency(maxAmount, 'TL')} olabilir.`,
        })
      }
      return false
    }
    return true
  }

  const handleSerialNumberCheck = async () => {
    dispatchState({ error: null })

    if (!validateSerialNumber()) return

    // Aynı seri numarası daha önce eklendi mi kontrol et
    const existingProduct = state.products.find(
      p => p.SeriNo?.toUpperCase() === state.serialNumber.trim().toUpperCase()
    )
    if (existingProduct) {
      dispatchState({ error: 'Bu seri numarası zaten eklenmiş.' })
      return
    }

    dispatchState({ loading: true })

    // Şirket kodunu al - companyCode veya companyValue'dan
    const selectedCompanyCode = state.companyCode || (state.companyValue?.value ? String(state.companyValue.value) : '')

    try {
      const request: GetSerialNumberCheck = {
        SerialNumber: state.serialNumber.trim(),
        DealerCode: props.user.AccountCode,
        CompanyCode: selectedCompanyCode,
        InvoiceNumber: '',
        IsCredit: true,
      }

      const response = await new GetSerialNumberCheckApi({}).checkSerialNumber(request)

      dispatchState({ loading: false })

      if (!response || response.length === 0) {
        // Seri numarası sorgusu başarısız olursa, manuel ürün girişi ekranına geç
        dispatchState({
          step: 'manual_product_entry',
          error: 'Seri numarası sistemde bulunamamıştır. Lütfen ürün bilgilerini manuel olarak giriniz.',
        })
        return
      }

      if (response.length > 1) {
        // Birden fazla kayıt gelirse manuel giriş ekranına yönlendir
        dispatchState({
          step: 'manual_product_entry',
          error:
            'Bu seri numarasına ait birden fazla kayıt bulunmaktadır. Lütfen ürün bilgilerini manuel olarak giriniz.',
        })
        return
      }

      // Tek kayıt - otomatik olarak bilgileri doldur
      const productData = response[0]

      // Varsayılan kredi tutarını hesapla
      const defaultAmount = productData.NETWRBR
      const rate = getExchangeRateValue(productData.CurrencyType)
      const amountTL = defaultAmount * rate

      // Ürünü listeye ekle
      const newProduct: ProductWithCreditInfo = {
        ...productData,
        creditAmount: defaultAmount,
        creditAmountTL: amountTL,
        inputCurrency: 'ORIGINAL',
      }

      const updatedProducts = [...state.products, newProduct]

      // Toplam kredi tutarlarını hesapla
      const totalAmount = updatedProducts.reduce((sum, p) => sum + p.creditAmount, 0)
      const totalAmountTL = updatedProducts.reduce((sum, p) => sum + p.creditAmountTL, 0)

      // Başarılı - ürünü ekle, ürün bilgisi sayfasına geç ve input'u temizle
      dispatchState({
        products: updatedProducts,
        serialNumberData: productData, // Geriye dönük uyumluluk
        creditAmount: totalAmount,
        creditAmountTL: totalAmountTL,
        serialNumber: '', // Input'u temizle, yeni seri no girmek için
        step: 'product_info',
      })
    } catch (err: any) {
      dispatchState({
        loading: false,
        error: err.description || ErrorMessages.NETWORK_ERROR,
      })
    }
  }

  // Ürünü listeden kaldır
  const handleRemoveProduct = (seriNo: string) => {
    const updatedProducts = state.products.filter(p => p.SeriNo !== seriNo)

    // Toplam kredi tutarlarını yeniden hesapla
    const totalAmount = updatedProducts.reduce((sum, p) => sum + p.creditAmount, 0)
    const totalAmountTL = updatedProducts.reduce((sum, p) => sum + p.creditAmountTL, 0)

    // Eğer hiç ürün kalmadıysa serial_input adımına dön
    if (updatedProducts.length === 0) {
      dispatchState({
        products: [],
        serialNumberData: null,
        creditAmount: 0,
        creditAmountTL: 0,
        step: 'serial_input',
      })
    } else {
      dispatchState({
        products: updatedProducts,
        serialNumberData: updatedProducts[0], // İlk ürünü referans olarak tut
        creditAmount: totalAmount,
        creditAmountTL: totalAmountTL,
      })
    }
  }

  // Belirli bir ürünün kredi tutarını güncelle
  const handleProductCreditAmountChange = (seriNo: string, amount: number, inputCurrency: 'ORIGINAL' | 'TL') => {
    const updatedProducts = state.products.map(product => {
      if (product.SeriNo !== seriNo) return product

      const rate = getExchangeRateValue(product.CurrencyType)
      let creditAmount: number
      let creditAmountTL: number

      if (inputCurrency === 'TL') {
        // TL girişi - 2 ondalık basamağa yuvarla
        creditAmountTL = Math.round(amount * 100) / 100
        creditAmount = Math.round((amount / rate) * 100) / 100
      } else {
        // Döviz girişi - 2 ondalık basamağa yuvarla
        creditAmount = Math.round(amount * 100) / 100
        creditAmountTL = Math.round(amount * rate * 100) / 100
      }

      return {
        ...product,
        creditAmount,
        creditAmountTL,
        inputCurrency,
      }
    })

    // Toplam tutarları hesapla - 2 ondalık basamağa yuvarla
    const totalAmount = Math.round(updatedProducts.reduce((sum, p) => sum + p.creditAmount, 0) * 100) / 100
    const totalAmountTL = Math.round(updatedProducts.reduce((sum, p) => sum + p.creditAmountTL, 0) * 100) / 100

    dispatchState({
      products: updatedProducts,
      creditAmount: totalAmount,
      creditAmountTL: totalAmountTL,
      error: null,
    })
  }

  const handleContinueToCustomerInfo = () => {
    dispatchState({ error: null })

    if (!validateCreditAmount()) return

    dispatchState({ step: 'customer_info' })
  }

  const handleCheckCredit = async () => {
    dispatchState({ error: null })

    if (!validateGsmNumber()) return

    dispatchState({ step: 'processing', loading: true })

    try {
      // Birden fazla ürün için Products dizisi oluştur
      const products: SerialCreditProductItem[] = state.products.map(product => {
        const currencyTypeForApi =
          product.isManualEntry && (product.CurrencyType || 'TL') === 'TL' ? 'TRY' : product.CurrencyType || 'TL'
        return {
          SerialNumber: product.SeriNo,
          CreditAmount: product.creditAmount,
          CreditAmountTL: product.creditAmountTL,
          CurrencyType: currencyTypeForApi,
          ExchangeRate: getExchangeRateValue(product.CurrencyType || 'TL'),
          ProductCode: product.ProductCode,
          InvoiceNumber: product.InvoiceNumber,
          ProductName: product.ProductName,
          Brand: product.Marka,
          IsManual: product.isManualEntry || false,
        }
      })

      // Toplam tutarları hesapla
      const totalCreditAmount = getTotalCreditAmount()
      const totalCreditAmountTL = getTotalCreditAmountTL()

      const request: SerialCreditApplicationRequest = {
        // Geriye dönük uyumluluk için tekil alanlar (tek ürün durumunda)
        SerialNumber: state.products.length === 1 ? state.products[0].SeriNo : undefined,
        CreditAmount: state.products.length === 1 ? state.products[0].creditAmount : undefined,
        CreditAmountTL: state.products.length === 1 ? state.products[0].creditAmountTL : undefined,
        CurrencyType:
          state.products.length === 1
            ? state.products[0].isManualEntry && (state.products[0].CurrencyType || 'TL') === 'TL'
              ? 'TRY'
              : state.products[0].CurrencyType
            : undefined,
        ExchangeRate:
          state.products.length === 1 ? getExchangeRateValue(state.products[0].CurrencyType || 'TL') : undefined,
        ProductCode: state.products.length === 1 ? state.products[0].ProductCode : undefined,
        InvoiceNumber: state.products.length === 1 ? state.products[0].InvoiceNumber : undefined,
        // Birden fazla ürün desteği
        Products: products,
        // Ortak alanlar
        CustomerGsmNumber: state.customerGsmNumber.replace(/\s/g, '').replace(/_/g, ''),
        DealerCode: props.user.AccountCode,
        CompanyCode: state.companyCode,
        TotalCreditAmount: totalCreditAmount,
        TotalCreditAmountTL: totalCreditAmountTL,
        identitNumber: state.identitNumber || undefined,
        dob: state.dob || undefined,
      }

      const response = await new CheckSerialCreditApi({}).checkCredit(request)

      dispatchState({ loading: false })

      // Response null veya undefined ise hata
      if (!response) {
        console.error('Response is null or undefined')
        dispatchState({
          step: 'error',
          error: ErrorMessages.NETWORK_ERROR,
          errorCode: null,
          checkCreditData: null
        })
        return
      }

      if (!response.Success) {
        dispatchState({
          step: 'error',
          error: response.ErrorMessage || ErrorMessages.UNKNOWN,
          errorCode: response.ErrorCode || null,
          checkCreditData: response.CheckCreditData || null
        })
        return
      }

      dispatchState({
        step: 'success-credit',
        checkCreditData: response.CheckCreditData || null
      })
    } catch (err: any) {
      console.error('CheckSerialCredit error:', err)
      console.error('Error type:', typeof err)
      console.error('Error keys:', err ? Object.keys(err) : 'null')

      // IException formatında hata
      if (err.description) {
        dispatchState({
          loading: false,
          step: 'error',
          error: err.description,
          errorCode: err.code || null,
          checkCreditData: null
        })
      }
      // Axios hata formatı
      else if (err.response?.data?.Message) {
        dispatchState({
          loading: false,
          step: 'error',
          error: err.response.data.Message,
          errorCode: null,
          checkCreditData: null
        })
      }
      // Standart Error objesi
      else if (err.message) {
        dispatchState({
          loading: false,
          step: 'error',
          error: err.message,
          errorCode: null,
          checkCreditData: null
        })
      }
      // Diğer hatalar
      else {
        dispatchState({
          loading: false,
          step: 'error',
          error: ErrorMessages.NETWORK_ERROR,
          checkCreditData: null
        })
      }
    }
  }

  const handleApplyCredit = async () => {
    dispatchState({ error: null })

    if (!validateGsmNumber()) return

    dispatchState({ step: 'processing', loading: true })

    try {
      // Birden fazla ürün için Products dizisi oluştur
      const products: SerialCreditProductItem[] = state.products.map(product => {
        const currencyTypeForApi =
          product.isManualEntry && (product.CurrencyType || 'TL') === 'TL' ? 'TRY' : product.CurrencyType || 'TL'
        return {
          SerialNumber: product.SeriNo,
          CreditAmount: product.creditAmount,
          CreditAmountTL: product.creditAmountTL,
          CurrencyType: currencyTypeForApi,
          ExchangeRate: getExchangeRateValue(product.CurrencyType || 'TL'),
          ProductCode: product.ProductCode,
          InvoiceNumber: product.InvoiceNumber,
          ProductName: product.ProductName,
          Brand: product.Marka,
          IsManual: product.isManualEntry || false,
        }
      })

      // Toplam tutarları hesapla
      const totalCreditAmount = getTotalCreditAmount()
      const totalCreditAmountTL = getTotalCreditAmountTL()

      const request: SerialCreditApplicationRequest = {
        // Geriye dönük uyumluluk için tekil alanlar (tek ürün durumunda)
        SerialNumber: state.products.length === 1 ? state.products[0].SeriNo : undefined,
        CreditAmount: state.products.length === 1 ? state.products[0].creditAmount : undefined,
        CreditAmountTL: state.products.length === 1 ? state.products[0].creditAmountTL : undefined,
        CurrencyType:
          state.products.length === 1
            ? state.products[0].isManualEntry && (state.products[0].CurrencyType || 'TL') === 'TL'
              ? 'TRY'
              : state.products[0].CurrencyType
            : undefined,
        ExchangeRate:
          state.products.length === 1 ? getExchangeRateValue(state.products[0].CurrencyType || 'TL') : undefined,
        ProductCode: state.products.length === 1 ? state.products[0].ProductCode : undefined,
        InvoiceNumber: state.products.length === 1 ? state.products[0].InvoiceNumber : undefined,
        // Birden fazla ürün desteği
        Products: products,
        // Ortak alanlar
        CustomerGsmNumber: state.customerGsmNumber.replace(/\s/g, '').replace(/_/g, ''),
        DealerCode: props.user.AccountCode,
        CompanyCode: state.companyCode,
        TotalCreditAmount: totalCreditAmount,
        TotalCreditAmountTL: totalCreditAmountTL,
      }

      console.log('ApplySerialCredit request:', request)

      const response = await new ApplySerialCreditApi({}).applyCredit(request)

      console.log('ApplySerialCredit response:', response)

      dispatchState({ loading: false })

      // Response null veya undefined ise hata
      if (!response) {
        console.error('Response is null or undefined')
        dispatchState({
          step: 'error',
          error: ErrorMessages.NETWORK_ERROR,
          errorCode: null,
        })
        return
      }

      if (!response.Success) {
        // Bekleyen başvuru varsa modal göster
        if (response.ErrorCode === ErrorCodes.CREDIT_ALREADY_USED && response.CanCancel) {
          dispatchState({
            loading: false,
            pendingOrderId: response.OrderId || null,
            pendingRedirectUrl: response.RedirectUrl || null,
          })
          showPendingCreditModal(response)
          return
        }

        dispatchState({
          step: 'error',
          error: response.ErrorMessage || ErrorMessages.UNKNOWN,
          errorCode: response.ErrorCode || null,
        })
        return
      }

      // Başarılı - HTML content ile ödeme sayfasına yönlendir
      dispatchState({
        step: 'success',
        applicationId: response.OrderId,
      })

      // HTML content varsa form submit ile yönlendir
      if (response.HtmlContent) {
        submitCompayHtmlContent(response.HtmlContent, response.RedirectUrl)
      } else if (response.RedirectUrl) {
        // Fallback: Direkt URL yönlendirmesi
        window.location.href = response.RedirectUrl
      }
    } catch (err: any) {
      console.error('ApplySerialCredit error:', err)
      console.error('Error type:', typeof err)
      console.error('Error keys:', err ? Object.keys(err) : 'null')

      // IException formatında hata
      if (err.description) {
        dispatchState({
          loading: false,
          step: 'error',
          error: err.description,
          errorCode: err.code || null,
        })
      }
      // Axios hata formatı
      else if (err.response?.data?.Message) {
        dispatchState({
          loading: false,
          step: 'error',
          error: err.response.data.Message,
          errorCode: null,
        })
      }
      // Standart Error objesi
      else if (err.message) {
        dispatchState({
          loading: false,
          step: 'error',
          error: err.message,
          errorCode: null,
        })
      }
      // Diğer hatalar
      else {
        dispatchState({
          loading: false,
          step: 'error',
          error: ErrorMessages.NETWORK_ERROR,
        })
      }
    }
  }

  const handleBack = () => {
    dispatchState({ error: null })

    switch (state.step) {
      case 'product_info':
        // Seri numarası girişine dönerken tüm ürünleri ve şirket seçimini de sıfırla
        dispatchState({
          step: 'serial_input',
          products: [],
          serialNumberData: null,
          creditAmount: 0,
          creditAmountTL: 0,
          companyValue: state.companyOptions?.length === 1 ? state.companyOptions[0] : undefined,
          companyCode: state.companyOptions?.length === 1 ? String(state.companyOptions[0].value) : '',
        })
        break
      case 'customer_info':
        // Ürün bilgisine dön
        dispatchState({ step: 'product_info' })
        break
      case 'error':
        // Hata ekranından dönerken tüm formu sıfırla
        dispatchState({
          step: 'serial_input',
          products: [],
          serialNumberData: null,
          serialNumber: '',
          creditAmount: 0,
          creditAmountTL: 0,
          customerGsmNumber: '',
          companyValue: state.companyOptions?.length === 1 ? state.companyOptions[0] : undefined,
          companyCode: state.companyOptions?.length === 1 ? String(state.companyOptions[0].value) : '',
        })
        break
      default:
        break
    }
  }

  const handleReset = () => {
    const { exchangeRate, profitMarginRate, minProfitMarginRate, categoryPriceLimits } = state
    dispatchState({ ...getInitialState(), exchangeRate, profitMarginRate, minProfitMarginRate, categoryPriceLimits })
    //getCompanies()
  }

  const handleCancelPendingCredit = async () => {
    dispatchState({ cancelLoading: true })

    try {
      const response = await new CancelSerialCreditApi({}).cancelCredit({
        SerialNumber: state.serialNumber.trim(),
        DealerCode: props.user.AccountCode,
      })

      dispatchState({ cancelLoading: false })

      if (response.Success) {
        Swal.fire({
          icon: 'success',
          title: 'Başvuru İptal Edildi',
          text: 'Bekleyen kredi başvurusu iptal edildi. Yeni başvuru yapabilirsiniz.',
          confirmButtonText: 'Tamam',
        }).then(() => {
          // İptal sonrası aynı bilgilerle tekrar başvuru yap
          handleApplyCredit()
        })
      } else {
        Swal.fire({
          icon: 'error',
          title: 'İptal Başarısız',
          text: response.Message || 'Başvuru iptal edilemedi.',
          confirmButtonText: 'Tamam',
        })
      }
    } catch (err: any) {
      dispatchState({ cancelLoading: false })
      Swal.fire({
        icon: 'error',
        title: 'Hata',
        text: err.description || 'Bağlantı hatası oluştu.',
        confirmButtonText: 'Tamam',
      })
    }
  }

  const showPendingCreditModal = (response: any) => {
    Swal.fire({
      icon: 'warning',
      title: 'Bekleyen Başvuru Bulundu',
      html: `
        <div style="text-align: left; padding: 10px 0;">
          <p>${response.ErrorMessage || ErrorMessages.CREDIT_ALREADY_USED}</p>
          ${response.OrderId ? `<p style="margin-top: 10px;"><strong>Başvuru No:</strong> ${response.OrderId}</p>` : ''}
          <p style="margin-top: 15px; color: #666;">Ne yapmak istersiniz?</p>
        </div>
      `,
      showCancelButton: true,
      showDenyButton: !!response.RedirectUrl,
      confirmButtonText: 'Başvuruyu İptal Et',
      denyButtonText: 'Mevcut Başvuruyu Takip Et',
      cancelButtonText: 'Vazgeç',
      confirmButtonColor: '#dc3545',
      denyButtonColor: '#0d6efd',
      cancelButtonColor: '#6c757d',
      reverseButtons: true,
    }).then(result => {
      if (result.isConfirmed) {
        // Başvuruyu iptal et
        handleCancelPendingCredit()
      } else if (result.isDenied && response.RedirectUrl) {
        // Mevcut başvuruyu takip et - yeni sekmede aç
        window.open(response.RedirectUrl, '_blank')
      }
      // Vazgeç - hiçbir şey yapma
    })
  }

  const submitCompayHtmlContent = (html: string, fallbackAction?: string) => {
    console.log('submitCompayHtmlContent çalışıyor')

    // Create hidden container
    const container = document.createElement('div')
    container.style.display = 'none'
    container.id = 'compay-serial-credit-container'
    document.body.appendChild(container)

    // Inject the HTML form
    container.innerHTML = html

    // Find the form
    const form = (container.querySelector('#compayForm') || container.querySelector('form')) as HTMLFormElement | null

    if (!form) {
      console.error('Form bulunamadı!')
      document.body.removeChild(container)
      Swal.fire('Hata', 'Compay ödeme formu bulunamadı!', 'error')
      return
    }

    // Ensure action exists
    if (!form.action && fallbackAction) {
      form.action = fallbackAction
    }

    // Security: allow only Compay domain
    try {
      const actionUrl = new URL(form.action)
      const allowedHosts = [
        'test.compaypayment.com',
        'compaypayment.com',
        'www.compaypayment.com',
        'cptest.asseco-see.com.tr',
      ]
      if (!allowedHosts.includes(actionUrl.hostname)) {
        throw new Error(`Invalid Compay form action URL: ${actionUrl.hostname}`)
      }
    } catch (error) {
      console.error('URL güvenlik hatası:', error)
      document.body.removeChild(container)
      Swal.fire('Hata', 'Geçersiz yönlendirme adresi!', 'error')
      return
    }

    // Submit form
    try {
      form.submit()
      console.log('Form başarıyla submit edildi')
    } catch (error) {
      console.error('Form submit hatası:', error)
      Swal.fire('Hata', 'Ödeme sayfasına yönlendirilirken bir hata oluştu!', 'error')
      document.body.removeChild(container)
    }
  }

  const formatCurrency = (amount: number, currency: string = 'TL'): string => {
    return `${amount.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`
  }

  const formatDate = (date: Date | string): string => {
    const d = new Date(date)
    return d.toLocaleDateString('tr-TR')
  }

  const getStepNumber = (): number => {
    switch (state.step) {
      case 'serial_input':
        return 1
      case 'manual_product_entry':
        return 1 // serial_input ile aynı adım
      case 'product_info':
        return 2
      case 'customer_info':
        return 3
      case 'processing':
      case 'success':
      case 'error':
        return 4
      default:
        return 1
    }
  }

  const renderStepIndicator = () => {
    const currentStep = getStepNumber()
    const steps = [
      { num: 1, label: 'Seri No Girişi' },
      { num: 2, label: 'Ürün Bilgisi' },
      { num: 3, label: 'Müşteri Bilgisi' },
      { num: 4, label: 'Sonuç' },
    ]

    return (
      <div className={styles.stepIndicator}>
        {steps.map(step => (
          <div
            key={step.num}
            className={`${styles.step} ${currentStep === step.num ? styles.active : ''} ${currentStep > step.num ? styles.completed : ''
              }`}
          >
            <div className={styles.stepNumber}>
              {currentStep > step.num ? <i className='fas fa-check'></i> : step.num}
            </div>
            <span className={styles.stepLabel}>{step.label}</span>
          </div>
        ))}
      </div>
    )
  }

  const handleManualProductSubmit = () => {
    dispatchState({ error: null })

    // Validation
    if (!state.manualProductCode.trim()) {
      dispatchState({ error: 'Lütfen ürün kodunu giriniz.' })
      return
    }
    if (!state.manualProductName.trim()) {
      dispatchState({ error: 'Lütfen ürün adını giriniz.' })
      return
    }
    if (!state.manualProductCategory) {
      dispatchState({ error: 'Lütfen ürün kategorisini seçiniz.' })
      return
    }
    if (!state.serialNumber.trim()) {
      dispatchState({ error: 'Lütfen seri numarasını giriniz.' })
      return
    }

    // Manuel ürün için GetSerialNumberCheckResponse-like object oluştur
    // Fiyat ikinci adımda (product_info) girilecek, burada 0 olarak başlat
    const manualProduct: ProductWithCreditInfo = {
      DealerCode: props.user.AccountCode,
      MatNumber: state.manualProductCode,
      InvoiceNumber: '',
      Date: new Date(),
      Currency: 0,
      NETWRBR: 0, // Fiyat product_info'da girilecek
      TaxRate: 0,
      CurrencyType: state.manualProductCurrency, // Kullanıcı seçtiği para birimini kullan
      DeliveryNumber: '',
      DeliveryCreationDate: '',
      SeriNo: state.serialNumber.trim(),
      EInvoiceNumber: '',
      Amount: 0,
      ProductCode: state.manualProductCode,
      ProductName: state.manualProductName,
      Marka: state.manualProductCategory,
      // ProductWithCreditInfo alanları
      creditAmount: 0, // Fiyat product_info'da girilecek
      creditAmountTL: 0,
      inputCurrency: state.manualProductCurrency === 'TL' ? 'TL' : 'ORIGINAL', // Para birimine göre set et
      isManualEntry: true, // Manuel olarak girildi, kar marjı uygulanmayacak
    }

    const updatedProducts = [...state.products, manualProduct]

    dispatchState({
      products: updatedProducts,
      serialNumberData: manualProduct,
      creditAmount: 0,
      creditAmountTL: 0,
      serialNumber: '',
      manualProductCode: '',
      manualProductName: '',
      manualProductPrice: '',
      manualProductCurrency: 'TL',
      manualProductCategory: '',
      step: 'product_info',
    })
  }

  const renderManualProductEntry = () => {
    const currencyOptions: ISelectOption[] = [
      { value: 'TL', label: 'TL' },
      { value: 'USD', label: 'USD' },
      { value: 'EUR', label: 'EUR' },
    ]

    // API'den gelen kategori verilerine göre seçenekler oluştur
    const categoryOptions: ISelectOption[] = state.categoryPriceLimits.map(cat => ({
      value: cat.Category,
      label: cat.Category,
    }))

    // Seçili kategorinin fiyat limitlerini bul
    const selectedCategoryLimit = state.categoryPriceLimits.find(
      cl => cl.Category?.toLowerCase() === state.manualProductCategory.toLowerCase()
    )

    return (
      <div className={styles.card}>
        <div className={styles.serialInputSection}>
          <div className={styles.iconWrapper}>
            <i className='fas fa-keyboard'></i>
          </div>
          <div className={styles.heading}>
            <h2>Ürün Bilgilerini Manuel Olarak Giriniz</h2>
            <p>
              Seri numarası sistemde bulunamadı. Lütfen ürün detaylarını aşağıda giriniz ve kredi başvurusuna devam
              ediniz.
            </p>
          </div>

          {state.error && (
            <div className={styles.errorAlert}>
              <i className='fas fa-exclamation-circle'></i>
              <span>{state.error}</span>
            </div>
          )}

          <div style={{ maxWidth: '500px', margin: '0 auto', width: '100%' }}>
            {/* Seri Numarası */}
            <div style={{ marginBottom: '20px' }}>
              <label
                style={{
                  display: 'block',
                  textAlign: 'left',
                  fontWeight: 600,
                  color: '#333',
                  marginBottom: '8px',
                  fontSize: '14px',
                }}
              >
                Seri Numarası *
              </label>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  type='text'
                  value={state.serialNumber}
                  readOnly
                  style={{
                    flex: '1',
                    padding: '10px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    backgroundColor: '#f5f5f5',
                    color: '#333',
                    cursor: 'default',
                  }}
                />
                <button
                  type='button'
                  onClick={() => dispatchState({ step: 'serial_input', error: null, serialNumber: '' })}
                  style={{
                    padding: '10px 14px',
                    background: '#6c757d',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '13px',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                  title='Farklı bir seri numarası sorgulamak için geri dönün'
                >
                  <i className='fas fa-sync-alt'></i>
                  Değiştir
                </button>
              </div>
            </div>

            {/* Ürün Kodu */}
            <div style={{ marginBottom: '20px' }}>
              <label
                style={{
                  display: 'block',
                  textAlign: 'left',
                  fontWeight: 600,
                  color: '#333',
                  marginBottom: '8px',
                  fontSize: '14px',
                }}
              >
                Ürün Kodu *
              </label>
              <input
                type='text'
                placeholder='Ürün kodunu giriniz'
                value={state.manualProductCode}
                onChange={e => dispatchState({ manualProductCode: e.target.value, error: null })}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            {/* Ürün Adı */}
            <div style={{ marginBottom: '20px' }}>
              <label
                style={{
                  display: 'block',
                  textAlign: 'left',
                  fontWeight: 600,
                  color: '#333',
                  marginBottom: '8px',
                  fontSize: '14px',
                }}
              >
                Ürün Adı *
              </label>
              <input
                type='text'
                placeholder='Ürün adını giriniz'
                value={state.manualProductName}
                onChange={e => dispatchState({ manualProductName: e.target.value, error: null })}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            {/* Para Birimi */}
            <div style={{ marginBottom: '20px' }}>
              <label
                style={{
                  display: 'block',
                  textAlign: 'left',
                  fontWeight: 600,
                  color: '#333',
                  marginBottom: '8px',
                  fontSize: '14px',
                }}
              >
                Para Birimi *
              </label>
              <IPSelectBox
                options={currencyOptions}
                value={currencyOptions.find(o => o.value === state.manualProductCurrency)}
                onChangeSingle={option =>
                  dispatchState({ manualProductCurrency: String(option?.value || 'TL'), error: null })
                }
              />
            </div>

            {/* Ürün Kategorisi */}
            <div style={{ marginBottom: '20px' }}>
              <label
                style={{
                  display: 'block',
                  textAlign: 'left',
                  fontWeight: 600,
                  color: '#333',
                  marginBottom: '8px',
                  fontSize: '14px',
                }}
              >
                Ürün Kategorisi *
              </label>
              <IPSelectBox
                options={categoryOptions}
                value={categoryOptions.find(o => o.value === state.manualProductCategory)}
                onChangeSingle={option =>
                  dispatchState({ manualProductCategory: String(option?.value || ''), error: null })
                }
              />
              {selectedCategoryLimit && (
                <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                  {selectedCategoryLimit.MinPriceLimit !== undefined &&
                    selectedCategoryLimit.MinPriceLimit !== null && (
                      <div>
                        Minimum Fiyat: {formatCurrency(selectedCategoryLimit.MinPriceLimit, 'TL')}
                        {state.manualProductCurrency &&
                          state.manualProductCurrency !== 'TL' &&
                          (() => {
                            const exchangeRateValue = getExchangeRateValue(state.manualProductCurrency)
                            return ` (≈ ${formatCurrency(
                              selectedCategoryLimit.MinPriceLimit / exchangeRateValue,
                              state.manualProductCurrency
                            )})`
                          })()}
                      </div>
                    )}
                  {selectedCategoryLimit.MaxPriceLimit !== undefined &&
                    selectedCategoryLimit.MaxPriceLimit !== null && (
                      <div>
                        Maksimum Fiyat: {formatCurrency(selectedCategoryLimit.MaxPriceLimit, 'TL')}
                        {state.manualProductCurrency &&
                          state.manualProductCurrency !== 'TL' &&
                          (() => {
                            const exchangeRateValue = getExchangeRateValue(state.manualProductCurrency)
                            return ` (≈ ${formatCurrency(
                              selectedCategoryLimit.MaxPriceLimit / exchangeRateValue,
                              state.manualProductCurrency
                            )})`
                          })()}
                      </div>
                    )}
                  {(!selectedCategoryLimit.MinPriceLimit || selectedCategoryLimit.MinPriceLimit === null) &&
                    (!selectedCategoryLimit.MaxPriceLimit || selectedCategoryLimit.MaxPriceLimit === null) && (
                      <div>Bu kategori için fiyat limiti bulunmamaktadır.</div>
                    )}
                </div>
              )}
            </div>

            {/* Butonlar */}
            <div style={{ display: 'flex', gap: '12px', marginTop: '30px' }}>
              <IPButton
                text='Geri Dön'
                onClick={() =>
                  dispatchState({
                    step: 'serial_input',
                    error: null,
                  })
                }
                style={{
                  flex: '1',
                  background: '#f0f0f0',
                  color: '#333',
                  border: '1px solid #ddd',
                  padding: '12px 24px',
                  fontSize: '14px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              />
              <IPButton
                text='Devam Et'
                onClick={handleManualProductSubmit}
                style={{
                  flex: '1',
                  background: 'linear-gradient(135deg, #007bff 0%, #0056b3 100%)',
                  border: 'none',
                  padding: '12px 24px',
                  fontSize: '14px',
                  borderRadius: '4px',
                  width: '100%',
                }}
              />
            </div>
          </div>
        </div>
      </div>
    )
  }

  const renderSerialInput = () => (
    <div className={styles.card}>
      <div className={styles.serialInputSection}>
        <div className={styles.iconWrapper}>
          <i className='fas fa-barcode'></i>
        </div>
        <div className={styles.heading}>
          <h2>Seri Numarası ile Kredi Başvurusu</h2>
          <p>
            Daha önce cari olarak satın aldığınız ürünlerin seri numaralarını girerek müşterinize alışveriş kredisi
            kullandırabilirsiniz. Birden fazla ürün ekleyebilirsiniz.
          </p>
        </div>

        {state.error && (
          <div className={styles.errorAlert}>
            <i className='fas fa-exclamation-circle'></i>
            <span>{state.error}</span>
          </div>
        )}

        {/* Form Alanları - Hizalı Container */}
        <div style={{ maxWidth: '500px', margin: '0 auto', width: '100%' }}>
          {/* Şirket Seçimi */}
          {/* <div style={{ marginBottom: '20px' }}>
            <label
              style={{
                display: 'block',
                textAlign: 'left',
                fontWeight: 600,
                color: '#333',
                marginBottom: '8px',
                fontSize: '14px',
              }}
            >
              Şirket
            </label>
            <IPSelectBox
              options={props.user.AccountCode === "0000203179" ? state.companyOptions : state.companyOptions.filter(co => co.value === 1100)}
              value={state.companyValue}
              isDisabled={state.companyOptions?.length === 1 || state.products.length > 0}
              onChangeSingle={option => {
                if (option) dispatchState({ companyValue: option, companyCode: String(option.value) })
                else dispatchState({ companyCode: '', companyValue: undefined })
              }}
              isClearable={state.products.length === 0}
            />
          </div> */}

          {/* Seri Numarası Giriş Alanı */}
          <div>
            <label
              style={{
                display: 'block',
                textAlign: 'left',
                fontWeight: 600,
                color: '#333',
                marginBottom: '10px',
                fontSize: '14px',
              }}
            >
              Seri Numarası
            </label>

            {/* Input Row */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                gap: '12px',
                width: '100%',
              }}
            >
              {/* Input Container */}
              <div style={{ flex: '1 1 auto', minWidth: '200px' }}>
                <input
                  type='text'
                  placeholder='Seri numarasını giriniz...'
                  value={state.serialNumber}
                  onChange={e => dispatchState({ serialNumber: e.target.value.toUpperCase(), error: null })}
                  onKeyPress={e => e.key === 'Enter' && handleSerialNumberCheck()}
                  style={{
                    width: '100%',
                    height: '50px',
                    padding: '0 16px',
                    fontSize: '16px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    outline: 'none',
                    boxSizing: 'border-box',
                    transition: 'border-color 0.2s ease',
                  }}
                  onFocus={e => (e.target.style.borderColor = '#007bff')}
                  onBlur={e => (e.target.style.borderColor = '#e0e0e0')}
                />
              </div>

              {/* Button Container */}
              <div style={{ flex: '0 0 auto' }}>
                <button
                  type='button'
                  onClick={handleSerialNumberCheck}
                  disabled={state.loading}
                  style={{
                    height: '50px',
                    padding: '0 24px',
                    fontSize: '15px',
                    fontWeight: 600,
                    color: '#fff',
                    background: state.loading ? '#6c757d' : 'linear-gradient(135deg, #28a745 0%, #1e7e34 100%)',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: state.loading ? 'not-allowed' : 'pointer',
                    whiteSpace: 'nowrap',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    minWidth: '120px',
                  }}
                >
                  {state.loading ? (
                    <>
                      <i className='fas fa-spinner fa-spin'></i>
                      Ekleniyor...
                    </>
                  ) : (
                    <>
                      <i className='fas fa-plus'></i>
                      Ürün Ekle
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Eklenen Ürünler Listesi */}
        {state.products.length > 0 && (
          <div style={{ marginTop: '20px', maxWidth: '500px', margin: '20px auto 0' }}>
            <h4 style={{ marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <i className='fas fa-shopping-cart'></i>
              Eklenen Ürünler ({state.products.length} adet)
            </h4>
            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
              {state.products.map((product, index) => (
                <div
                  key={product.SeriNo}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '12px 15px',
                    marginBottom: '8px',
                    background: '#f8f9fa',
                    borderRadius: '8px',
                    border: '1px solid #e0e0e0',
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 'bold', fontSize: '14px' }}>{product.ProductName}</div>
                    <div style={{ fontSize: '12px', color: '#888', marginBottom: '5px' }}>{product.ProductCode}</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      Seri: {product.SeriNo} | {product.Marka}
                    </div>
                    <div style={{ fontSize: '13px', color: '#28a745', fontWeight: '600' }}>
                      {formatCurrency(product.NETWRBR, product.CurrencyType)}
                    </div>
                  </div>
                  <button
                    type='button'
                    onClick={() => handleRemoveProduct(product.SeriNo)}
                    style={{
                      background: '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: '50%',
                      width: '28px',
                      height: '28px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginLeft: '10px',
                    }}
                    title='Ürünü Kaldır'
                  >
                    <i className='fas fa-times' style={{ fontSize: '12px' }}></i>
                  </button>
                </div>
              ))}
            </div>
            {/* Devam Et Butonu */}
            <div style={{ marginTop: '15px' }}>
              <IPButton
                text='Kredi Tutarlarını Belirle ve Devam Et'
                onClick={() => dispatchState({ step: 'product_info' })}
                style={{
                  background: 'linear-gradient(135deg, #007bff 0%, #0056b3 100%)',
                  border: 'none',
                  padding: '15px 40px',
                  fontSize: '16px',
                  borderRadius: '8px',
                  width: '100%',
                }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  )

  const renderProductInfo = () => {
    const { products, exchangeRate } = state
    if (products.length === 0) return null

    // Toplam tutarları hesapla
    const totalCreditAmountTL = getTotalCreditAmountTL()

    // Ürün kartı render fonksiyonu
    const renderProductCard = (product: ProductWithCreditInfo, index: number) => {
      // Her render'da state'den güncel ürün verisini al (closure sorununu önler)
      const currentProduct = state.products.find(p => p.SeriNo === product.SeriNo) ?? product

      const currencyType = currentProduct.CurrencyType?.toUpperCase()
      const isForeignCurrency =
        currencyType === 'USD' || currencyType === 'DOLAR' || currencyType === 'EUR' || currencyType === 'EURO'
      const exchangeRateValue = getExchangeRateValue(currentProduct.CurrencyType)
      const maxCreditAmount = getMaxCreditAmountForProduct(currentProduct)
      const maxCreditAmountTL = maxCreditAmount * exchangeRateValue
      const minCreditAmount = getMinCreditAmountForProduct(currentProduct)
      const minCreditAmountTL = minCreditAmount * exchangeRateValue
      const basePrice = currentProduct.NETWRBR
      const basePriceTL = basePrice * exchangeRateValue

      // Kullanıcının giriş yaptığı para birimi için görüntülenecek değer
      const displayInputValue =
        currentProduct.inputCurrency === 'TL' ? currentProduct.creditAmountTL : currentProduct.creditAmount
      const inputCurrencyLabel = currentProduct.inputCurrency === 'TL' ? 'TL' : currentProduct.CurrencyType

      return (
        <div
          key={currentProduct.SeriNo}
          className={styles.productCard}
          style={{
            marginBottom: '20px',
            border: '1px solid #e0e0e0',
            borderRadius: '12px',
            padding: '20px',
            position: 'relative',
          }}
        >
          {/* Ürün Silme Butonu */}
          <button
            type='button'
            onClick={() => handleRemoveProduct(currentProduct.SeriNo)}
            style={{
              position: 'absolute',
              top: '10px',
              right: '10px',
              background: '#dc3545',
              color: 'white',
              border: 'none',
              borderRadius: '50%',
              width: '30px',
              height: '30px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            title='Ürünü Kaldır'
          >
            <i className='fas fa-times'></i>
          </button>

          <div className={styles.productHeader} style={{ marginBottom: '15px' }}>
            <div className={styles.brandLogo}>
              <i className='fas fa-box'></i>
            </div>
            <div className={styles.productDetails}>
              <h3 style={{ margin: 0, fontSize: '16px' }}>{currentProduct.ProductName}</h3>
              <div style={{ fontSize: '13px', color: '#888', marginBottom: '8px' }}>{currentProduct.ProductCode}</div>
              <div className={styles.brand}>{currentProduct.Marka}</div>
              <p className={styles.description} style={{ margin: '5px 0 0 0', color: '#666', fontSize: '14px' }}>
                Seri No: {currentProduct.SeriNo}
              </p>
            </div>
          </div>

          <div
            className={styles.infoGrid}
            style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginBottom: '15px' }}
          >
            <div className={styles.infoItem}>
              <div className={styles.label} style={{ fontSize: '12px', color: '#666' }}>
                Birim Tutar
              </div>
              <div className={styles.value} style={{ fontWeight: 'bold' }}>
                {formatCurrency(basePrice, currentProduct.CurrencyType)}
                {isForeignCurrency && (
                  <div className={styles.tlEquivalent} style={{ fontSize: '11px', color: '#888' }}>
                    ≈ {formatCurrency(basePriceTL, 'TL')}
                  </div>
                )}
              </div>
            </div>
            <div className={styles.infoItem}>
              <div className={styles.label} style={{ fontSize: '12px', color: '#666' }}>
                Fatura No
              </div>
              <div className={styles.value}>{currentProduct.InvoiceNumber}</div>
            </div>
            {!currentProduct.isManualEntry && (
              <div className={styles.infoItem}>
                <div className={styles.label} style={{ fontSize: '12px', color: '#666' }}>
                  Min. Kredi
                </div>
                <div className={styles.value} style={{ color: '#fd7e14', fontWeight: 'bold' }}>
                  {formatCurrency(minCreditAmount, currentProduct.CurrencyType)}
                  {isForeignCurrency && (
                    <div style={{ fontSize: '11px', color: '#888' }}>≈ {formatCurrency(minCreditAmountTL, 'TL')}</div>
                  )}
                </div>
              </div>
            )}
            {!currentProduct.isManualEntry && (
              <div className={styles.infoItem}>
                <div className={styles.label} style={{ fontSize: '12px', color: '#666' }}>
                  Maks. Kredi
                </div>
                <div className={styles.value} style={{ color: '#28a745', fontWeight: 'bold' }}>
                  {formatCurrency(maxCreditAmount, currentProduct.CurrencyType)}
                  {isForeignCurrency && (
                    <div style={{ fontSize: '11px', color: '#888' }}>≈ {formatCurrency(maxCreditAmountTL, 'TL')}</div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Manuel Ürün için Fiyat Girişi */}
          {currentProduct.isManualEntry && (
            <div
              style={{
                marginBottom: '20px',
                padding: '15px',
                background: '#f8f9fa',
                borderRadius: '8px',
                border: '1px solid #e0e0e0',
              }}
            >
              <label
                style={{ fontWeight: '600', fontSize: '14px', display: 'block', marginBottom: '12px', color: '#333' }}
              >
                Ürün Fiyatı *
              </label>

              <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                <div style={{ flex: '0 0 120px' }}>
                  <select
                    value={currentProduct.CurrencyType}
                    onChange={e => {
                      const newCurrency = e.target.value as 'TL' | 'USD' | 'EUR'
                      // Para birimi değişince girilen sayı sabit kalır, TL karşılığı yeniden hesaplanır
                      const latest = state.products.find(p => p.SeriNo === currentProduct.SeriNo)!
                      const currentAmount = latest.NETWRBR // Kullanıcının girdiği sayı sabit
                      const newRate = getExchangeRateValue(newCurrency)
                      const newCreditAmountTL =
                        newCurrency === 'TL' ? currentAmount : Math.round(currentAmount * newRate * 100) / 100
                      const updatedProducts = state.products.map(p =>
                        p.SeriNo === currentProduct.SeriNo
                          ? {
                            ...p,
                            CurrencyType: newCurrency,
                            NETWRBR: currentAmount,
                            Amount: currentAmount,
                            creditAmount: currentAmount,
                            creditAmountTL: newCreditAmountTL,
                            inputCurrency: (newCurrency === 'TL' ? 'TL' : 'ORIGINAL') as 'TL' | 'ORIGINAL',
                          }
                          : p
                      )
                      const newTotalCreditAmountTL = updatedProducts.reduce((sum, p) => sum + p.creditAmountTL, 0)
                      dispatchState({ products: updatedProducts, creditAmountTL: newTotalCreditAmountTL, error: null })
                    }}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '13px',
                      boxSizing: 'border-box',
                      backgroundColor: '#fff',
                      cursor: 'pointer',
                    }}
                  >
                    <option value='TL'>TL</option>
                    <option value='USD'>USD</option>
                    <option value='EUR'>EUR</option>
                  </select>
                </div>

                {/* Fiyat Input */}
                <input
                  type='number'
                  placeholder='0.00'
                  value={currentProduct.NETWRBR > 0 ? currentProduct.NETWRBR.toString() : ''}
                  onChange={e => {
                    let inputValue = e.target.value
                    let value = parseFloat(inputValue) || 0
                    value = Math.round(value * 100) / 100
                    if (value < 0) value = 0

                    const latest = state.products.find(p => p.SeriNo === currentProduct.SeriNo)!
                    const categoryLimit = state.categoryPriceLimits.find(
                      cl => cl.Category?.toLowerCase() === latest.Marka?.toLowerCase()
                    )

                    let error: string | null = null
                    if (categoryLimit && value > 0) {
                      const exchangeRateValue = getExchangeRateValue(latest.CurrencyType)
                      const valueTL = value * exchangeRateValue
                      if (categoryLimit.MinPriceLimit !== undefined && categoryLimit.MinPriceLimit !== null) {
                        if (valueTL < categoryLimit.MinPriceLimit) {
                          const minInOriginalCurrency = categoryLimit.MinPriceLimit / exchangeRateValue
                          error = `Bu kategoriye ait ürünün fiyatı minimum ${formatCurrency(
                            categoryLimit.MinPriceLimit,
                            'TL'
                          )} olmalıdır.${latest.CurrencyType !== 'TL'
                            ? ` (Minimum ${formatCurrency(minInOriginalCurrency, latest.CurrencyType)})`
                            : ''
                            }`
                        }
                      }
                      if (categoryLimit.MaxPriceLimit !== undefined && categoryLimit.MaxPriceLimit !== null) {
                        if (valueTL > categoryLimit.MaxPriceLimit) {
                          const maxInOriginalCurrency = categoryLimit.MaxPriceLimit / exchangeRateValue
                          error = `Bu kategoriye ait ürünün fiyatı maksimum ${formatCurrency(
                            categoryLimit.MaxPriceLimit,
                            'TL'
                          )} olmalıdır.${latest.CurrencyType !== 'TL'
                            ? ` (Maksimum ${formatCurrency(maxInOriginalCurrency, latest.CurrencyType)})`
                            : ''
                            }`
                        }
                      }
                    }

                    if (error) {
                      dispatchState({ error })
                      return
                    }

                    const exchangeRateValue = getExchangeRateValue(latest.CurrencyType)
                    const priceInSelectedCurrency = value
                    const priceTL =
                      latest.CurrencyType === 'TL'
                        ? priceInSelectedCurrency
                        : Math.round(priceInSelectedCurrency * exchangeRateValue * 100) / 100

                    const updatedProducts = state.products.map(p =>
                      p.SeriNo === currentProduct.SeriNo
                        ? {
                          ...p,
                          NETWRBR: priceInSelectedCurrency,
                          Amount: priceInSelectedCurrency,
                          creditAmount: priceInSelectedCurrency,
                          creditAmountTL: priceTL,
                          inputCurrency: (latest.CurrencyType === 'TL' ? 'TL' : 'ORIGINAL') as 'TL' | 'ORIGINAL',
                        }
                        : p
                    )
                    const newTotalCreditAmountTL = updatedProducts.reduce((sum, p) => sum + p.creditAmountTL, 0)
                    dispatchState({ products: updatedProducts, creditAmountTL: newTotalCreditAmountTL, error: null })
                  }}
                  step='0.01'
                  min='0'
                  style={{
                    flex: 1,
                    padding: '10px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '13px',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              {/* Kategori Fiyat Limitleri Gösterimi */}
              {(() => {
                const categoryLimit = state.categoryPriceLimits.find(
                  cl => cl.Category?.toLowerCase() === currentProduct.Marka?.toLowerCase()
                )
                if (!categoryLimit) return null
                const exchangeRateValue = getExchangeRateValue(currentProduct.CurrencyType)
                return (
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
                    {categoryLimit.MinPriceLimit !== undefined && categoryLimit.MinPriceLimit !== null && (
                      <div>
                        Min: {formatCurrency(categoryLimit.MinPriceLimit, 'TL')}
                        {currentProduct.CurrencyType !== 'TL' &&
                          ` (≈ ${formatCurrency(
                            categoryLimit.MinPriceLimit / exchangeRateValue,
                            currentProduct.CurrencyType
                          )})`}
                      </div>
                    )}
                    {categoryLimit.MaxPriceLimit !== undefined && categoryLimit.MaxPriceLimit !== null && (
                      <div>
                        Max: {formatCurrency(categoryLimit.MaxPriceLimit, 'TL')}
                        {currentProduct.CurrencyType !== 'TL' &&
                          ` (≈ ${formatCurrency(
                            categoryLimit.MaxPriceLimit / exchangeRateValue,
                            currentProduct.CurrencyType
                          )})`}
                      </div>
                    )}
                  </div>
                )
              })()}
            </div>
          )}

          {/* Kredi Tutarı Girişi */}
          <div className={styles.amountInput}>
            <div
              className={styles.amountInputHeader}
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}
            >
              <label style={{ fontWeight: '600', fontSize: '14px' }}>Kredi Tutarı</label>
              {isForeignCurrency && (
                <div className={styles.currencyToggle} style={{ display: 'flex', gap: '4px' }}>
                  <button
                    type='button'
                    className={`${styles.currencyBtn} ${currentProduct.inputCurrency === 'ORIGINAL' ? styles.active : ''
                      }`}
                    onClick={() => {
                      const latest = state.products.find(p => p.SeriNo === currentProduct.SeriNo)
                      if (!latest || latest.inputCurrency !== 'TL') return
                      const rate = getExchangeRateValue(latest.CurrencyType)
                      const recalculated = Math.round((latest.creditAmountTL / rate) * 100) / 100
                      handleProductCreditAmountChange(latest.SeriNo, recalculated, 'ORIGINAL')
                    }}
                    style={{
                      padding: '4px 8px',
                      fontSize: '12px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      background: currentProduct.inputCurrency === 'ORIGINAL' ? '#007bff' : '#fff',
                      color: currentProduct.inputCurrency === 'ORIGINAL' ? '#fff' : '#333',
                      cursor: 'pointer',
                    }}
                  >
                    {currentProduct.CurrencyType}
                  </button>
                  <button
                    type='button'
                    className={`${styles.currencyBtn} ${currentProduct.inputCurrency === 'TL' ? styles.active : ''}`}
                    onClick={() => {
                      const latest = state.products.find(p => p.SeriNo === currentProduct.SeriNo)
                      if (!latest || latest.inputCurrency !== 'ORIGINAL') return
                      const rate = getExchangeRateValue(latest.CurrencyType)
                      const convertedToTL = Math.round(latest.creditAmount * rate * 100) / 100
                      handleProductCreditAmountChange(latest.SeriNo, convertedToTL, 'TL')
                    }}
                    style={{
                      padding: '4px 8px',
                      fontSize: '12px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      background: currentProduct.inputCurrency === 'TL' ? '#007bff' : '#fff',
                      color: currentProduct.inputCurrency === 'TL' ? '#fff' : '#333',
                      cursor: 'pointer',
                    }}
                  >
                    TL
                  </button>
                </div>
              )}
            </div>

            <div className={styles.inputWithCurrency} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <input
                type='number'
                value={isForeignCurrency ? displayInputValue.toString() : currentProduct.creditAmountTL.toString()}
                disabled={currentProduct.isManualEntry === true}
                onChange={e => {
                  if (currentProduct.isManualEntry) return
                  const latest = state.products.find(p => p.SeriNo === currentProduct.SeriNo)
                  if (!latest) return

                  let inputValue = e.target.value
                  const parts = inputValue.split('.')
                  if (parts[1] && parts[1].length > 2) {
                    inputValue = parts[0] + '.' + parts[1].substring(0, 2)
                  }

                  let value = parseFloat(inputValue) || 0
                  value = Math.round(value * 100) / 100

                  const rate = getExchangeRateValue(latest.CurrencyType)
                  const maxForProduct = getMaxCreditAmountForProduct(latest)
                  const maxForProductTL = maxForProduct * rate

                  const maxValue = isForeignCurrency
                    ? latest.inputCurrency === 'TL'
                      ? maxForProductTL
                      : maxForProduct
                    : maxForProductTL

                  if (value > maxValue) {
                    value = Math.round(maxValue * 100) / 100
                  }

                  if (!isForeignCurrency) {
                    handleProductCreditAmountChange(latest.SeriNo, value, 'TL')
                  } else {
                    handleProductCreditAmountChange(latest.SeriNo, value, latest.inputCurrency)
                  }
                }}
                step='0.01'
                min={0}
                max={
                  isForeignCurrency
                    ? currentProduct.inputCurrency === 'TL'
                      ? maxCreditAmountTL
                      : maxCreditAmount
                    : maxCreditAmountTL
                }
                style={{
                  flex: 1,
                  height: '48px',
                  padding: '0 16px',
                  fontSize: '16px',
                  border: currentProduct.isManualEntry ? '2px solid #e0e0e0' : '2px solid #e0e0e0',
                  borderRadius: '8px',
                  outline: 'none',
                  boxSizing: 'border-box',
                  backgroundColor: currentProduct.isManualEntry ? '#f5f5f5' : '#fff',
                  color: currentProduct.isManualEntry ? '#888' : '#333',
                  cursor: currentProduct.isManualEntry ? 'not-allowed' : 'text',
                }}
                onFocus={e => {
                  if (!currentProduct.isManualEntry) e.target.style.borderColor = '#007bff'
                }}
                onBlur={e => {
                  e.target.style.borderColor = '#e0e0e0'
                }}
              />
              <span className={styles.currencyLabel} style={{ fontWeight: 'bold', minWidth: '40px' }}>
                {isForeignCurrency ? inputCurrencyLabel : 'TL'}
              </span>
            </div>

            {/* Karşılık Gösterimi */}
            {isForeignCurrency && (currentProduct.creditAmount > 0 || currentProduct.creditAmountTL > 0) && (
              <div className={styles.tlEquivalentBox} style={{ marginTop: '8px', fontSize: '13px', color: '#666' }}>
                <i className='fas fa-coins' style={{ marginRight: '5px' }}></i>
                {currentProduct.inputCurrency === 'TL' ? (
                  <span>
                    {currentProduct.CurrencyType} Karşılığı:{' '}
                    <strong>{formatCurrency(currentProduct.creditAmount, currentProduct.CurrencyType)}</strong>
                  </span>
                ) : (
                  <span>
                    TL Karşılığı: <strong>{formatCurrency(currentProduct.creditAmountTL, 'TL')}</strong>
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      )
    }

    return (
      <div className={styles.card + ' ' + styles.productCard}>
        <div className={styles.productInfoSection}>
          <div className={styles.productHeader} style={{ marginBottom: '20px' }}>
            <div className={styles.brandLogo}>
              <i className='fas fa-shopping-cart'></i>
            </div>
            <div className={styles.productDetails}>
              <h3>Seçilen Ürünler ({products.length} adet)</h3>
              <p className={styles.description}>Aşağıdaki ürünler için kredi başvurusu yapılacaktır.</p>
            </div>
          </div>

          {/* Seçili Şirket Bilgisi */}
          {/* <div
            style={{
              background: '#e3f2fd',
              padding: '12px 16px',
              borderRadius: '8px',
              marginBottom: '15px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              border: '1px solid #90caf9',
            }}
          >
            <i className='fas fa-building' style={{ color: '#1976d2', fontSize: '18px' }}></i>
            <div>
              <span style={{ fontSize: '12px', color: '#666' }}>Seçili Şirket:</span>
              <strong style={{ marginLeft: '8px', color: '#1976d2', fontSize: '15px' }}>
                {state.companyValue?.label || state.companyCode}
              </strong>
            </div>
            <div
              style={{
                marginLeft: 'auto',
                fontSize: '11px',
                color: '#666',
                background: '#fff',
                padding: '4px 8px',
                borderRadius: '4px',
              }}
            >
              <i className='fas fa-info-circle' style={{ marginRight: '4px' }}></i>
              Sadece bu şirketten ürün eklenebilir
            </div>
          </div> */}

          {/* Yeni Ürün Ekleme Alanı */}
          <div
            style={{
              background: '#f8f9fa',
              padding: '20px',
              borderRadius: '8px',
              marginBottom: '20px',
              border: '2px dashed #007bff',
            }}
          >
            <label
              style={{
                display: 'block',
                fontWeight: 600,
                color: '#333',
                marginBottom: '10px',
                fontSize: '14px',
              }}
            >
              <i className='fas fa-plus-circle' style={{ marginRight: '8px', color: '#007bff' }}></i>
              {/* Yeni Ürün Ekle ({state.companyValue?.label || state.companyCode}) */}
              Yeni Ürün Ekle
            </label>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                gap: '12px',
                width: '100%',
              }}
            >
              {/* Input Container */}
              <div style={{ flex: '1 1 auto', minWidth: '200px' }}>
                <input
                  type='text'
                  placeholder='Seri numarasını giriniz...'
                  value={state.serialNumber}
                  onChange={e => dispatchState({ serialNumber: e.target.value.toUpperCase(), error: null })}
                  onKeyPress={e => e.key === 'Enter' && handleSerialNumberCheck()}
                  style={{
                    width: '100%',
                    height: '48px',
                    padding: '0 16px',
                    fontSize: '15px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    outline: 'none',
                    boxSizing: 'border-box',
                    backgroundColor: '#fff',
                    transition: 'border-color 0.2s ease',
                  }}
                  onFocus={e => (e.target.style.borderColor = '#007bff')}
                  onBlur={e => (e.target.style.borderColor = '#e0e0e0')}
                />
              </div>

              {/* Button Container */}
              <div style={{ flex: '0 0 auto' }}>
                <button
                  type='button'
                  onClick={handleSerialNumberCheck}
                  disabled={state.loading}
                  style={{
                    height: '48px',
                    padding: '0 20px',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#fff',
                    background: state.loading ? '#6c757d' : 'linear-gradient(135deg, #28a745 0%, #1e7e34 100%)',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: state.loading ? 'not-allowed' : 'pointer',
                    whiteSpace: 'nowrap',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    minWidth: '110px',
                  }}
                >
                  {state.loading ? (
                    <>
                      <i className='fas fa-spinner fa-spin'></i>
                      Ekleniyor...
                    </>
                  ) : (
                    <>
                      <i className='fas fa-plus'></i>
                      Ürün Ekle
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Döviz Kuru Bilgisi */}
          {exchangeRate && (
            <div
              className={styles.exchangeRateInfo}
              style={{ marginBottom: '15px', padding: '10px', background: '#e3f2fd', borderRadius: '8px' }}
            >
              <i className='fas fa-exchange-alt' style={{ marginRight: '8px' }}></i>
              <span>
                Güncel Kurlar: 1 USD = {parseExchangeRate(exchangeRate.Usd).toFixed(4)} TL | 1 EUR ={' '}
                {parseExchangeRate(exchangeRate.Eur).toFixed(4)} TL
              </span>
              <small style={{ marginLeft: '10px', color: '#666' }}>
                (Güncelleme: {new Date(exchangeRate.WhenUpdated).toLocaleTimeString('tr-TR')})
              </small>
            </div>
          )}

          {state.error && (
            <div
              className={styles.errorAlert}
              style={{
                marginBottom: '15px',
                padding: '12px',
                background: '#f8d7da',
                border: '1px solid #f5c6cb',
                borderRadius: '8px',
                color: '#721c24',
              }}
            >
              <i className='fas fa-exclamation-circle' style={{ marginRight: '8px' }}></i>
              <span>{state.error}</span>
            </div>
          )}

          {/* Ürün Listesi */}
          <div className={styles.productsList}>
            {products.map((product, index) => renderProductCard(product, index))}
          </div>

          {/* Toplam Özet */}
          <div
            style={{
              background: '#e8f5e9',
              padding: '20px',
              borderRadius: '12px',
              marginTop: '20px',
              border: '2px solid #28a745',
            }}
          >
            <h4 style={{ margin: '0 0 15px 0', color: '#28a745', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <i className='fas fa-calculator'></i>
              Kredi Özeti
            </h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px' }}>
              <div>
                <div style={{ fontSize: '13px', color: '#666' }}>Toplam Ürün Sayısı</div>
                <div style={{ fontSize: '18px', fontWeight: 'bold' }}>{products.length} Adet</div>
              </div>
              <div>
                <div style={{ fontSize: '13px', color: '#666' }}>Toplam Kredi Tutarı (TL)</div>
                <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#28a745' }}>
                  {formatCurrency(totalCreditAmountTL, 'TL')}
                </div>
              </div>
            </div>
            <div
              style={{
                marginTop: '15px',
                padding: '12px',
                background: '#fff',
                borderRadius: '6px',
                border: '1px solid #28a745',
                fontSize: '12px',
                color: '#333',
              }}
            >
              <div style={{ marginBottom: '8px' }}>
                <i className='fas fa-info-circle' style={{ marginRight: '6px', color: '#28a745' }}></i>
                <strong>Kredi Aracı Kuruluşu Komisyonu: %1.25</strong>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '6px', fontSize: '11px' }}>
                <div>Toplam Kredi Tutarı: {formatCurrency(totalCreditAmountTL, 'TL')}</div>
                <div style={{ color: '#dc3545' }}>
                  - Komisyon: {formatCurrency(Math.round(totalCreditAmountTL * 0.0125 * 100) / 100, 'TL')}
                </div>
                <div style={{ borderTop: '1px solid #ddd', paddingTop: '6px', fontWeight: 'bold', color: '#28a745' }}>
                  = Net Ödenecek Tutar:{' '}
                  {formatCurrency(Math.round((totalCreditAmountTL - totalCreditAmountTL * 0.0125) * 100) / 100, 'TL')}
                </div>
              </div>
            </div>
            <div style={{ marginTop: '15px', fontSize: '13px', color: '#dc3545' }}>
              <strong>Not:</strong> Ödeme toplam {formatCurrency(totalCreditAmountTL, 'TL')} tutarı üzerinden Türk
              Lirası olarak alınacaktır.
            </div>
          </div>

          <div
            className={styles.buttonGroup}
            style={{ marginTop: '25px', display: 'flex', gap: '15px', justifyContent: 'space-between' }}
          >
            <button
              className={styles.btnSecondary}
              onClick={handleBack}
              style={{
                padding: '12px 25px',
                borderRadius: '8px',
                border: '1px solid #6c757d',
                background: '#fff',
                color: '#6c757d',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <i className='fas fa-arrow-left'></i>
              Geri Dön
            </button>
            <button
              className={styles.btnPrimary}
              onClick={handleContinueToCustomerInfo}
              style={{
                padding: '12px 25px',
                borderRadius: '8px',
                border: 'none',
                background: 'linear-gradient(135deg, #007bff 0%, #0056b3 100%)',
                color: '#fff',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              Devam Et
              <i className='fas fa-arrow-right'></i>
            </button>
          </div>
        </div>
      </div>
    )
  }

  const renderCustomerInfo = () => {
    const { products } = state
    if (products.length === 0) return null

    // Toplam tutarları hesapla
    const totalCreditAmountTL = getTotalCreditAmountTL()

    return (
      <div className={styles.card}>
        <div className={styles.customerInfoSection}>
          <div className={styles.heading}>
            <h2>Müşteri Bilgileri</h2>
            <p>Ödeme linki aşağıdaki telefon numarasına SMS olarak gönderilecektir.</p>
          </div>

          {state.error && (
            <div className={styles.errorAlert}>
              <i className='fas fa-exclamation-circle'></i>
              <span>{state.error}</span>
            </div>
          )}

          <div className={styles.phoneInput}>
            <label>Müşteri Telefon Numarası</label>
            <div className={styles.inputGroup}>
              <span className={styles.prefix}>+90</span>
              <InputMask
                mask='999 999 99 99'
                placeholder='5XX XXX XX XX'
                value={state.customerGsmNumber}
                onChange={e => dispatchState({ customerGsmNumber: e.target.value, error: null })}
              />
            </div>
            <div className={styles.infoText}>
              <i className='fas fa-info-circle'></i>
              <span>Ödeme linki bu numaraya SMS ile gönderilecektir.</span>
            </div>
          </div>

          <div className={`${styles.card} ${styles.summaryCard}`}>
            <div className={styles.summarySection}>
              <h4>Başvuru Özeti ({products.length} Ürün)</h4>

              {/* Ürün Listesi */}
              {products.map((product, index) => {
                const currencyType = product.CurrencyType?.toUpperCase()
                const isForeignCurrency =
                  currencyType === 'USD' ||
                  currencyType === 'DOLAR' ||
                  currencyType === 'EUR' ||
                  currencyType === 'EURO'

                return (
                  <div
                    key={product.SeriNo}
                    style={{
                      padding: '12px',
                      background: index % 2 === 0 ? '#f8f9fa' : '#fff',
                      borderRadius: '8px',
                      marginBottom: '10px',
                    }}
                  >
                    <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#333' }}>
                      {index + 1}. {product.ProductName}
                    </div>
                    <div style={{ fontSize: '12px', color: '#888', marginBottom: '8px' }}>
                      {product.ProductCode} - {product.Marka}
                    </div>
                    <div
                      style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px', fontSize: '13px' }}
                    >
                      <div>
                        <span style={{ color: '#666' }}>Seri No: </span>
                        <span>{product.SeriNo}</span>
                      </div>
                      <div>
                        <span style={{ color: '#666' }}>Fatura No: </span>
                        <span>{product.InvoiceNumber}</span>
                      </div>
                      {isForeignCurrency && (
                        <div>
                          <span style={{ color: '#666' }}>Kredi ({product.CurrencyType}): </span>
                          <span>{formatCurrency(product.creditAmount, product.CurrencyType)}</span>
                        </div>
                      )}
                      <div>
                        <span style={{ color: '#666' }}>Kredi (TL): </span>
                        <span style={{ fontWeight: 'bold', color: '#28a745' }}>
                          {formatCurrency(product.creditAmountTL, 'TL')}
                        </span>
                      </div>
                    </div>
                  </div>
                )
              })}

              {/* Toplam */}
              <div
                className={`${styles.summaryItem} ${styles.total}`}
                style={{
                  marginTop: '15px',
                  paddingTop: '15px',
                  borderTop: '2px solid #28a745',
                }}
              >
                <span className={styles.label}>
                  <i className='fas fa-coins' style={{ marginRight: '8px' }}></i>
                  Toplam Kredi Tutarı (TL)
                </span>
                <span className={styles.value} style={{ color: '#28a745', fontSize: '1.5rem', fontWeight: 'bold' }}>
                  {formatCurrency(totalCreditAmountTL, 'TL')}
                </span>
              </div>
            </div>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <div className={styles.paymentNote}>
              <i className='fas fa-info-circle'></i>
              <span>
                Kredi ödemesi <strong>Türk Lirası</strong> üzerinden toplam{' '}
                <strong>{formatCurrency(totalCreditAmountTL, 'TL')}</strong> tutarında alınacaktır.
              </span>
            </div>
            <div
              style={{
                marginTop: '12px',
                padding: '12px',
                background: '#fff9e6',
                borderRadius: '6px',
                border: '1px solid #ffc107',
                fontSize: '12px',
                color: '#333',
              }}
            >
              <div style={{ marginBottom: '8px' }}>
                <i className='fas fa-percent' style={{ marginRight: '6px', color: '#ffc107' }}></i>
                <strong>Kredi Aracı Kuruluşu Komisyonu Bilgisi</strong>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '6px', fontSize: '11px' }}>
                <div>Toplam Kredi Tutarı: {formatCurrency(totalCreditAmountTL, 'TL')}</div>
                <div style={{ color: '#666' }}>
                  - Komisyon (%1.25): {formatCurrency(Math.round(totalCreditAmountTL * 0.0125 * 100) / 100, 'TL')}
                </div>
                <div style={{ borderTop: '1px solid #ddd', paddingTop: '6px', fontWeight: 'bold', color: '#28a745' }}>
                  = Net Ödenecek Tutar:{' '}
                  {formatCurrency(Math.round((totalCreditAmountTL - totalCreditAmountTL * 0.0125) * 100) / 100, 'TL')}
                </div>
              </div>
            </div>
          </div>

          <div className={styles.buttonGroup}>
            <button className={styles.btnSecondary} onClick={handleBack}>
              <i className='fas fa-arrow-left' style={{ marginRight: '8px' }}></i>
              Geri Dön
            </button>
            <button className={styles.btnPrimary} onClick={handleApplyCredit}>
              <i className='fas fa-paper-plane' style={{ marginRight: '8px' }}></i>
              Kredi Başvurusunu Gönder
            </button>
          </div>


          <br></br>
          <div className={styles.heading}>
            <h2>Kredi Durumu Sorgulama</h2>
          </div>

          <div className={styles.inlineInputRow}>
            <div className={styles.phoneInput}>
              <label>Müşteri Telefon Numarası</label>
              <div className={styles.inputGroup}>
                <span className={styles.prefix}>+90</span>
                <InputMask
                  mask='999 999 99 99'
                  placeholder='5XX XXX XX XX'
                  value={state.customerGsmNumber}
                  onChange={e => dispatchState({ customerGsmNumber: e.target.value, error: null })}
                />
              </div>
            </div>

            <div className={styles.phoneInput}>
              <label>Müşteri Kimlik Numarası</label>
              <div className={styles.inputGroup} style={{ marginTop: '15px' }}>
                <InputMask
                  mask='99999999999'
                  placeholder='1********11'
                  value={state.identitNumber}
                  onChange={e => dispatchState({ identitNumber: e.target.value, error: null })}
                />
              </div>
            </div>

            <div className={styles.phoneInput}>
              <label>Doğum Tarihi</label>
              <div className={styles.inputGroup}>
                <InputCampaign
                  type='date'
                  id='dob'
                  name='dob'
                  label=''
                  isActive
                  value={state.dob}
                  onChange={e => dispatchState({ dob: e.target.value, error: null })}
                />
              </div>
            </div>
          </div>

          <div className={styles.buttonGroup}>
            <button className={styles.btnPrimary} onClick={handleCheckCredit}>
              <i className='fas fa-paper-plane' style={{ marginRight: '8px' }}></i>
              Kredi Durumu Sorgula
            </button>
          </div>

        </div>
      </div>
    )
  }

  const renderProcessing = () => (
    <div className={styles.card}>
      <div className={styles.processingSection}>
        <div className={`${styles.iconWrapper} ${styles.loading}`}>
          <Spinner loading={true} />
        </div>
        <h3>Başvurunuz İşleniyor</h3>
        <p>Lütfen bekleyiniz, kredi başvurunuz işleme alınıyor...</p>
      </div>
    </div>
  )

  const renderSuccess = () => (
    <div className={styles.card}>
      <div className={styles.resultSection}>
        <div className={`${styles.iconWrapper} ${styles.success}`}>
          <i className='fas fa-check-circle'></i>
        </div>
        <h3>Başvuru Başarılı!</h3>
        <p>Kredi başvurusu başarıyla iletildi. Müşterinize SMS ile ödeme linki gönderilecektir.</p>
        {state.applicationId && (
          <div className={styles.applicationId}>
            Başvuru No: <strong>{state.applicationId}</strong>
          </div>
        )}
        <div className={styles.buttonGroup}>
          <button className={styles.btnPrimary} onClick={handleReset}>
            <i className='fas fa-plus' style={{ marginRight: '8px' }}></i>
            Yeni Başvuru
          </button>
        </div>
      </div>
    </div>
  )

  const renderSuccessCredit = () => (
    <>
      {Array.isArray(state.checkCreditData) && state.checkCreditData.length > 0 ? (
        state.checkCreditData.map((bankData, bankIndex) => (
          <div
            key={bankData.applicationId || bankIndex}
            className="credit-bank-card"
            style={{
              padding: '20px',
              border: '1px solid #e2e8f0',
              borderRadius: '12px',
              backgroundColor: '#ffffff',
              marginTop: '20px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
            }}
          >

            {/* Banka Başlık Alanı */}
            <div className="bank-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #edf2f7', paddingBottom: '15px', marginBottom: '15px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {bankData.logoPath && (
                  <img
                    src={bankData.logoPath}
                    alt={bankData.bankName}
                    style={{ height: '35px', objectFit: 'contain' }}
                  />
                )}
                <div>
                  <h3 style={{ margin: 0, fontSize: '18px', color: '#1a202c', fontWeight: '600' }}>
                    {bankData.bankName}
                  </h3>
                  {bankData.isSimulation && (
                    <span style={{ fontSize: '11px', color: '#dd6b20', backgroundColor: '#fffaf0', padding: '1px 5px', borderRadius: '4px', fontWeight: '500' }}>
                      Simülasyon Modu
                    </span>
                  )}
                </div>
              </div>

              {bankData.applicationId && (
                <span style={{ fontSize: '13px', color: '#718096' }}>
                  Başvuru ID: <strong>{bankData.applicationId}</strong>
                </span>
              )}
            </div>

            {/* Bu Bankaya Ait Tekliflerin Tablosu */}
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '14px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f7fafc', color: '#4a5568', borderBottom: '2px solid #edf2f7' }}>
                    <th style={{ padding: '12px 8px', fontWeight: '600' }}>Vade</th>
                    <th style={{ padding: '12px 8px', fontWeight: '600' }}>Kredi Tutarı</th>
                    <th style={{ padding: '12px 8px', fontWeight: '600' }}>Faiz Oranı</th>
                    <th style={{ padding: '12px 8px', fontWeight: '600' }}>Aylık Taksit</th>
                    <th style={{ padding: '12px 8px', fontWeight: '600' }}>Toplam Ödeme</th>
                  </tr>
                </thead>
                <tbody>
                  {bankData.offers && bankData.offers.length > 0 ? (
                    bankData.offers.map((offer, offerIndex) => (
                      <tr
                        key={offerIndex}
                        style={{ borderBottom: '1px solid #edf2f7', color: '#2d3748' }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f7fafc'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        <td style={{ padding: '14px 8px', fontWeight: '600' }}>{offer.term} Ay</td>
                        <td style={{ padding: '14px 8px' }}>{offer.loanAmount?.toLocaleString('tr-TR')} TL</td>
                        <td style={{ padding: '14px 8px', color: '#3182ce', fontWeight: '500' }}>%{offer.interestRate}</td>
                        <td style={{ padding: '14px 8px', fontWeight: '600' }}>{offer.installmentAmount?.toLocaleString('tr-TR')} TL</td>
                        <td style={{ padding: '14px 8px', fontWeight: '600', color: '#2f855a' }}>{offer.totalPaymentAmount?.toLocaleString('tr-TR')} TL</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} style={{ padding: '20px', textAlign: 'center', color: '#a0aec0' }}>
                        Bu bankaya ait uygun teklif bulunamadı.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ))
      ) : (
        <div style={{ padding: '20px', textAlign: 'center', color: '#718096', border: '1px dashed #e2e8f0', borderRadius: '8px', marginTop: '20px' }}>
          Listelenecek kredi teklifi bulunamadı.
        </div>
      )}
      <div className={styles.card}>
        <div className={styles.customerInfoSection}>
          <div className={styles.heading}>
            <h2>Başvuruyu Tamamla</h2>
            <p>Kredi başvurusunu tamamlamak için aşağıdaki bilgileri kontrol edin.</p>
          </div>

          <div className={styles.phoneInput}>
            <label>Müşteri Telefon Numarası</label>
            <div className={styles.inputGroup}>
              <span className={styles.prefix}>+90</span>
              <InputMask
                mask='999 999 99 99'
                placeholder='5XX XXX XX XX'
                value={state.customerGsmNumber}
                disabled={true}
              />
            </div>
            <div className={styles.infoText}>
              <i className='fas fa-info-circle'></i>
              <span>Bu telefon numarasına ödeme linki SMS ile gönderilecektir.</span>
            </div>
          </div>

          <div className={styles.buttonGroup}>
            <button className={styles.btnSecondary} onClick={handleReset}>
              <i className='fas fa-plus' style={{ marginRight: '8px' }}></i>
              Yeni Başvuru
            </button>
            <button className={styles.btnPrimary} onClick={handleApplyCredit}>
              <i className='fas fa-check' style={{ marginRight: '8px' }}></i>
              Başvuruyu Tamamla
            </button>
          </div>
        </div>
      </div>
    </>
  )

  const renderError = () => {
    const isPendingCreditError = state.errorCode === 'CREDIT_ALREADY_USED'

    return (
      <div className={styles.card}>
        <div className={styles.resultSection}>
          <div className={`${styles.iconWrapper} ${styles.error}`}>
            <i className='fas fa-times-circle'></i>
          </div>
          <h3>İşlem Başarısız</h3>
          <p>{state.error || ErrorMessages.UNKNOWN}</p>

          {isPendingCreditError && (
            <div className={styles.cancelSection}>
              <p className={styles.cancelInfo}>
                <i className='fas fa-info-circle'></i>
                Bu seri numarası için bekleyen bir kredi başvurusu bulunmaktadır. Yeni başvuru yapabilmek için mevcut
                başvuruyu kredi panelinden iptal edebilirsiniz.
              </p>
              {/* <button className={styles.btnDanger} onClick={handleCancelPendingCredit} disabled={state.cancelLoading}>
                {state.cancelLoading ? (
                  <>
                    <i className='fas fa-spinner fa-spin' style={{ marginRight: '8px' }}></i>
                    İptal Ediliyor...
                  </>
                ) : (
                  <>
                    <i className='fas fa-ban' style={{ marginRight: '8px' }}></i>
                    Bekleyen Başvuruyu İptal Et
                  </>
                )}
              </button> */}
            </div>
          )}

          <div className={styles.buttonGroup}>
            <button className={styles.btnSecondary} onClick={handleBack}>
              <i className='fas fa-arrow-left' style={{ marginRight: '8px' }}></i>
              Geri Dön
            </button>
            <button className={styles.btnPrimary} onClick={handleReset}>
              <i className='fas fa-redo' style={{ marginRight: '8px' }}></i>
              Tekrar Dene
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Kredi onaylandı - Makbuz göster
  const renderResultSuccess = () => (
    <div className={styles.card}>
      <div className={styles.resultSection}>
        <div className={`${styles.iconWrapper} ${styles.success}`}>
          <i className='fas fa-check-circle'></i>
        </div>
        <h3>Kredi Başvurusu Onaylandı!</h3>
        <p>Müşterinizin kredi başvurusu başarıyla tamamlandı.</p>

        {/* Makbuz Bilgileri */}
        <div className={styles.receiptCard}>
          <div className={styles.receiptHeader}>
            <i className='fas fa-receipt'></i>
            <span>İşlem Makbuzu</span>
          </div>
          <div className={styles.receiptBody}>
            {state.resultData?.orderId && (
              <div className={styles.receiptRow}>
                <span className={styles.receiptLabel}>Başvuru No:</span>
                <span className={styles.receiptValue}>{state.resultData.orderId}</span>
              </div>
            )}
            {state.resultData?.authCode && (
              <div className={styles.receiptRow}>
                <span className={styles.receiptLabel}>Onay Kodu:</span>
                <span className={styles.receiptValue}>{state.resultData.authCode}</span>
              </div>
            )}
            {state.resultData?.refNum && (
              <div className={styles.receiptRow}>
                <span className={styles.receiptLabel}>Referans No:</span>
                <span className={styles.receiptValue}>{state.resultData.refNum}</span>
              </div>
            )}
            {state.resultData?.amount && (
              <div className={styles.receiptRow}>
                <span className={styles.receiptLabel}>Tutar:</span>
                <span className={styles.receiptValue} style={{ color: '#28a745', fontWeight: 'bold' }}>
                  {formatCurrency(state.resultData.amount, 'TL')}
                </span>
              </div>
            )}
            {state.serialNumberData && (
              <>
                <div className={styles.receiptDivider}></div>
                <div className={styles.receiptRow}>
                  <span className={styles.receiptLabel}>Ürün:</span>
                  <span className={styles.receiptValue}>{state.serialNumberData.ProductCode}</span>
                </div>
                <div className={styles.receiptRow}>
                  <span className={styles.receiptLabel}>Seri No:</span>
                  <span className={styles.receiptValue}>{state.serialNumber}</span>
                </div>
              </>
            )}
          </div>
          <div className={styles.receiptFooter}>
            <i className='fas fa-clock'></i>
            <span>{new Date().toLocaleString('tr-TR')}</span>
          </div>
        </div>

        <div className={styles.buttonGroup}>
          <button className={styles.btnPrimary} onClick={handleReset}>
            <i className='fas fa-plus' style={{ marginRight: '8px' }}></i>
            Yeni Başvuru
          </button>
        </div>
      </div>
    </div>
  )

  // Kredi reddedildi - Hata göster
  const renderResultFail = () => (
    <div className={styles.card}>
      <div className={styles.resultSection}>
        <div className={`${styles.iconWrapper} ${styles.error}`}>
          <i className='fas fa-times-circle'></i>
        </div>
        <h3>Kredi Başvurusu Reddedildi</h3>
        <p>Müşterinizin kredi başvurusu onaylanmadı.</p>

        {state.resultData?.orderId && (
          <div className={styles.applicationId}>
            Başvuru No: <strong>{state.resultData.orderId}</strong>
          </div>
        )}

        {state.resultData?.errorMessage && (
          <div className={styles.errorDetail}>
            <i className='fas fa-exclamation-triangle'></i>
            <span>{state.resultData.errorMessage}</span>
          </div>
        )}

        <div className={styles.buttonGroup}>
          <button
            className={styles.btnSecondary}
            onClick={() => {
              // Müşteri bilgisi ekranına dönerken TL karşılığını yeniden hesapla
              if (state.serialNumberData) {
                const rate = getExchangeRateValue(state.serialNumberData.CurrencyType)
                dispatchState({
                  step: 'customer_info',
                  creditAmountTL: state.creditAmount * rate,
                  error: null,
                })
              } else {
                dispatchState({ step: 'customer_info', error: null })
              }
            }}
          >
            <i className='fas fa-arrow-left' style={{ marginRight: '8px' }}></i>
            Tekrar Dene
          </button>
          <button className={styles.btnPrimary} onClick={handleReset}>
            <i className='fas fa-plus' style={{ marginRight: '8px' }}></i>
            Yeni Başvuru
          </button>
        </div>
      </div>
    </div>
  )

  // Kredi beklemede - Yeni müşteri akışı (banka onboarding devam ediyor)
  const renderResultPending = () => (
    <div className={styles.card}>
      <div className={styles.resultSection}>
        <div className={`${styles.iconWrapper} ${styles.pending}`}>
          <i className='fas fa-hourglass-half'></i>
        </div>
        <h3>Kredi Başvurunuz Değerlendiriliyor</h3>
        <p>
          Müşterinizin banka onboarding süreci (video bankacılık veya kurye ile müşteri olma adımı) tamamlandığında
          başvuru sonuçlanacaktır. Bu aşamada kredi henüz kullandırılmamıştır; lütfen ürün teslimi yapmayınız. Sonuç
          bilgisi tarafınıza iletilecek ve kredi panelinden takip edilebilecektir.
        </p>

        {state.resultData?.orderId && (
          <div className={styles.applicationId}>
            Başvuru No: <strong>{state.resultData.orderId}</strong>
          </div>
        )}

        {state.resultData?.errorMessage && (
          <div className={styles.errorDetail}>
            <i className='fas fa-info-circle'></i>
            <span>{state.resultData.errorMessage}</span>
          </div>
        )}

        <div className={styles.buttonGroup}>
          <button className={styles.btnPrimary} onClick={handleReset}>
            <i className='fas fa-plus' style={{ marginRight: '8px' }}></i>
            Yeni Başvuru
          </button>
        </div>
      </div>
    </div>
  )

  const renderContent = () => {
    switch (state.step) {
      case 'serial_input':
        return renderSerialInput()
      case 'manual_product_entry':
        return renderManualProductEntry()
      case 'product_info':
        return renderProductInfo()
      case 'customer_info':
        return renderCustomerInfo()
      case 'processing':
        return renderProcessing()
      case 'success':
        return renderSuccess()
      case 'success-credit':
        return renderSuccessCredit()
      case 'error':
        return renderError()
      case 'result_success':
        return renderResultSuccess()
      case 'result_fail':
        return renderResultFail()
      case 'result_pending':
        return renderResultPending()
      default:
        return renderSerialInput()
    }
  }

  return (
    <div className={styles.serialCreditApplication}>
      <div className={styles.container}>
        {renderStepIndicator()}
        {renderContent()}
      </div>
    </div>
  )
}

export default componentWithContext(SerialCreditApplication)
